/**
 * Lead Persistence Functions
 * 
 * Standalone functions for persisting leads to the database.
 * Used by the orchestrator and other non-React contexts.
 */

import { supabase } from '@/integrations/supabase/client';
import type { LifecycleLead } from '../leadStateMachine';
import type { Json } from '@/integrations/supabase/types';

// Type for the status enum in the database
type DbLeadStatus = "NEW" | "ROUTING_IN_PROGRESS" | "ROUTED" | "UNSOLD" | "RETRY_QUEUED" | "STOCKED" | "ARCHIVED" | "REVIEW_REQUIRED" | "CREDITED";

// Helper to serialize validation errors to JSON
function serializeValidationErrors(errors: LifecycleLead['validationErrors']): Json | null {
  if (!errors || errors.length === 0) return null;
  return errors.map(e => ({
    field: e.field,
    message: e.message,
    code: e.code,
    severity: e.severity,
  })) as Json;
}

// Helper to serialize routing context to JSON
// CRITICAL: Serialize ALL fields for deterministic retry behavior
function serializeRoutingContext(ctx: LifecycleLead['routingContext']): Json | null {
  if (!ctx) return null;
  
  // Build the full context including per-agent overrides and injection metadata
  const serialized: Record<string, unknown> = {
    devOverridesEnabled: ctx.devOverridesEnabled,
    capturedAt: ctx.capturedAt,
    source: ctx.source,
  };
  
  // Include global override flags if set
  if (ctx.forceAllAgentsEligible !== undefined) {
    serialized.forceAllAgentsEligible = ctx.forceAllAgentsEligible;
  }
  if (ctx.forceWalletBalance !== undefined) {
    serialized.forceWalletBalance = ctx.forceWalletBalance;
  }
  if (ctx.forceLicenses && ctx.forceLicenses.length > 0) {
    serialized.forceLicenses = ctx.forceLicenses;
  }
  
  // Include per-agent overrides (critical for deterministic retries)
  if (ctx.perAgentOverrides) {
    serialized.perAgentOverrides = ctx.perAgentOverrides;
  }
  
  // Include DEV eligibility injection metadata
  if (ctx.eligibilitySource) {
    serialized.eligibilitySource = ctx.eligibilitySource;
  }
  if (ctx.devInjectedAgentIds && ctx.devInjectedAgentIds.length > 0) {
    serialized.devInjectedAgentIds = ctx.devInjectedAgentIds;
  }
  
  return serialized as Json;
}

// --- Create Lead ---

export async function persistLeadToDb(lead: LifecycleLead): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .insert({
      id: lead.id,
      lead_type_id: lead.leadTypeId,
      lead_type_name: lead.leadTypeName,
      vertical: lead.vertical,
      state: lead.state,
      estimated_value: lead.estimatedValue,
      status: lead.status as DbLeadStatus,
      retry_count: lead.retryCount,
      max_retries: lead.maxRetries,
      next_retry_at: lead.nextRetryAt ?? null,
      original_price: lead.originalPrice ?? null,
      current_price: lead.currentPrice ?? null,
      pricing_snapshot: lead.pricingSnapshot as Json ?? null,
      lead_payload: lead.leadPayload as Json ?? null,
      lead_profile: lead.leadProfile as Json ?? null,
      routing_context: serializeRoutingContext(lead.routingContext),
      routed_to_agent_id: lead.assignedAgentId ?? null,
      routed_to_agent_name: lead.assignedAgentName ?? null,
      charged_amount: lead.chargedAmount ?? null,
      contract_id: lead.contractId ?? null,
      validation_errors: serializeValidationErrors(lead.validationErrors),
      created_at: lead.createdAt,
      last_updated_at: lead.lastUpdatedAt,
    } as any);
  
  if (error) {
    console.error('[PersistLead] Failed to create lead:', error);
    throw error;
  }
}

// --- Update Lead ---

export async function updateLeadInDb(lead: LifecycleLead): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .update({
      lead_type_id: lead.leadTypeId,
      lead_type_name: lead.leadTypeName,
      vertical: lead.vertical,
      state: lead.state,
      estimated_value: lead.estimatedValue,
      status: lead.status as DbLeadStatus,
      retry_count: lead.retryCount,
      max_retries: lead.maxRetries,
      next_retry_at: lead.nextRetryAt ?? null,
      original_price: lead.originalPrice ?? null,
      current_price: lead.currentPrice ?? null,
      pricing_snapshot: lead.pricingSnapshot as Json ?? null,
      lead_payload: lead.leadPayload as Json ?? null,
      lead_profile: lead.leadProfile as Json ?? null,
      routing_context: serializeRoutingContext(lead.routingContext),
      routed_to_agent_id: lead.assignedAgentId ?? null,
      routed_to_agent_name: lead.assignedAgentName ?? null,
      charged_amount: lead.chargedAmount ?? null,
      contract_id: lead.contractId ?? null,
      validation_errors: serializeValidationErrors(lead.validationErrors),
      last_updated_at: lead.lastUpdatedAt,
    } as any)
    .eq('id', lead.id);
  
  if (error) {
    console.error('[PersistLead] Failed to update lead:', error);
    throw error;
  }
}

// --- Upsert Lead (create or update) ---

export async function upsertLeadToDb(lead: LifecycleLead): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .upsert({
      id: lead.id,
      lead_type_id: lead.leadTypeId,
      lead_type_name: lead.leadTypeName,
      vertical: lead.vertical,
      state: lead.state,
      estimated_value: lead.estimatedValue,
      status: lead.status as DbLeadStatus,
      retry_count: lead.retryCount,
      max_retries: lead.maxRetries,
      next_retry_at: lead.nextRetryAt ?? null,
      original_price: lead.originalPrice ?? null,
      current_price: lead.currentPrice ?? null,
      pricing_snapshot: lead.pricingSnapshot as Json ?? null,
      lead_payload: lead.leadPayload as Json ?? null,
      lead_profile: lead.leadProfile as Json ?? null,
      routing_context: serializeRoutingContext(lead.routingContext),
      routed_to_agent_id: lead.assignedAgentId ?? null,
      routed_to_agent_name: lead.assignedAgentName ?? null,
      charged_amount: lead.chargedAmount ?? null,
      contract_id: lead.contractId ?? null,
      validation_errors: serializeValidationErrors(lead.validationErrors),
      created_at: lead.createdAt,
      last_updated_at: lead.lastUpdatedAt,
    } as any, {
      onConflict: 'id',
    });
  
  if (error) {
    console.error('[PersistLead] Failed to upsert lead:', error);
    throw error;
  }
}

// --- Persist Review Required Lead ---

export async function persistReviewRequiredLead(lead: LifecycleLead): Promise<void> {
  if (lead.status !== 'REVIEW_REQUIRED') {
    throw new Error('Lead must have REVIEW_REQUIRED status');
  }
  
  await persistLeadToDb(lead);
  console.log('[PersistLead] Persisted REVIEW_REQUIRED lead:', lead.id);
}
